import { NotificationDropdown } from 'modules/layouts/LoggedLayout/components/Topbar/subcomponents/NotificationDropdown';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from 'shared/components/IconButton/IconButton';
import { useApplicationSearchParam } from 'shared/hooks/useApplicationSearchParam';
import { useBasePathname } from 'shared/hooks/useBasePathname';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';
import { useSitemap } from 'shared/hooks/useSitemap';
import { BellIcon, BellWithDotIcon, EyeIcon, HamburgerIcon } from 'shared/icons';
import { ConnectisLogo } from 'shared/icons/ConnectisLogo';
import ellipseLeftSrc from './assets/ellipse-left.svg';
import ellipseRightSrc from './assets/ellipse-right.svg';

const pageTitleKeys: Record<string, string> = {
  '/': 'offers',
  '/applications': 'applications',
  '/profile': 'profile',
  '/settings': 'settings',
  '/notifications': 'offers',
  '/observed': 'offers',
  '/job-orders': 'offers',
};

const getPageTitleKey = (path: keyof typeof pageTitleKeys, isApplicationParamPresent: boolean) => {
  if (path === '/job-orders' && isApplicationParamPresent) {
    return 'applications';
  }
  return pageTitleKeys[path];
};

interface TopbarProps {
  onHamburgerClick: () => void;
}

export const Topbar: FC<TopbarProps> = memo(({ onHamburgerClick }) => {
  const navigate = useNavigate();
  const sitemap = useSitemap();
  const { t } = useTranslation();
  const basePathname = useBasePathname();
  const { notifications } = useNotifications();
  const { isApplicationParamPresent } = useApplicationSearchParam();
  const isMobile = useBreakpoint('md');

  const notificationToggleButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const anyUnviewedNotification = useMemo(
    () => notifications.some(({ viewedAt }) => !viewedAt),
    [notifications],
  );

  const handleBellClick = useCallback(() => {
    if (isMobile) {
      navigate(sitemap.notifications);
      return;
    }
    setNotificationsOpen((prev) => !prev);
  }, [isMobile]);

  const topbarLabel = useMemo(
    () => getPageTitleKey(basePathname, isApplicationParamPresent),
    [basePathname, isApplicationParamPresent],
  );

  return (
    <>
      <NotificationDropdown
        buttonRef={notificationToggleButtonRef}
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
      <div className="fixed z-40 flex w-full items-center justify-between overflow-hidden bg-custom-gradient-mobile bg-[length:105.33%_289.29%] bg-[position:54%_-68px] px-4 py-3 md:px-6 lg:w-[calc(100dvw-256px-16px)] lg:rounded-b-2xl lg:bg-primary-gradient-horizontal lg:bg-auto lg:bg-center lg:py-3.5">
        <img
          alt=""
          src={ellipseLeftSrc}
          className="pointer-events-none absolute left-0 top-0 -z-10 hidden h-full lg:block"
        />
        <img
          alt=""
          src={ellipseRightSrc}
          className="pointer-events-none absolute bottom-0 right-0 -z-10 hidden h-full lg:block"
        />
        <h1 className="hidden text-base font-bold text-lightBlue lg:block">
          {t(`pageTitles:${topbarLabel}`)}
        </h1>
        <Link className="lg:hidden" to={sitemap.main}>
          <ConnectisLogo className="w-[120px]" />
        </Link>
        <div className="flex gap-4">
          <Link className="hidden lg:flex" to={sitemap.observed}>
            <IconButton name="observedRedirectButton" icon={EyeIcon} />
          </Link>
          <IconButton
            ref={notificationToggleButtonRef}
            name="notificationsToggleButton"
            icon={anyUnviewedNotification ? BellWithDotIcon : BellIcon}
            onClick={handleBellClick}
          />
          <IconButton
            onClick={onHamburgerClick}
            className="flex lg:hidden"
            name="notificationsToggleButton"
            icon={HamburgerIcon}
          />
        </div>
      </div>
    </>
  );
});
